import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SOCKET_CLIENT } from "../../../../utils/socket";
import { SOCKET_EVENTS } from "../../../../utils/Constants";
import { getStreamComments, getStreamDetails } from "../../services/stream.service";
import moment from "moment";
import { Placeholder } from "rsuite";
import { getUser } from "../../../../utils/commonfunction";
import AvatarImage from "../../components/AvatarImage";

export default function StaffComments({ passedStreamId, isModal, onClose }) {
    const { streamId: paramStreamId } = useParams();
    const streamId = passedStreamId || paramStreamId;
    const navigate = useNavigate();
    const userData = getUser() || {};
    const userID = userData?._id;

    const [comments, setComments] = useState([]);
    const [streamMeta, setStreamMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inputMessage, setInputMessage] = useState("");
    const [replyTo, setReplyTo] = useState(null);
 
     const commentsEndRef = useRef(null);

    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [comments]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [metaResp, commentsResp] = await Promise.all([
                getStreamDetails(streamId),
                getStreamComments(streamId)
            ]);

            if (metaResp.success) {
                setStreamMeta(metaResp.data);
            }

            if (commentsResp.success && commentsResp.data) {
                setComments(commentsResp.data.comments || []);
            }
        } catch (err) {
            console.error("Error fetching staff comment data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!streamId) return;
        fetchData();

        // Join room
        const joinRooms = () => {
            SOCKET_CLIENT.emit("join_stream_room", streamId);
            if (userID) {
                SOCKET_CLIENT.emit("join_stream_room", userID);
            }
        };

        if (SOCKET_CLIENT.connected) {
            joinRooms();
        }
        SOCKET_CLIENT.on("connect", joinRooms);

        SOCKET_CLIENT.on(SOCKET_EVENTS.NEW_COMMENT, (data) => {
            console.log("💬 Staff Screen: New comment received:", data);
            setComments((prev) => [...prev, data]);
        });

        // Also listen to PRIVATE_REPLY so staff sees their own sent private replies confirmed by server
        SOCKET_CLIENT.on(SOCKET_EVENTS.PRIVATE_REPLY, (data) => {
            console.log("🔒 Staff Screen: Private reply confirmed:", data);
            // Only add if not already shown (avoid duplicate with optimistic update)
            setComments((prev) => {
                const alreadyExists = prev.some(c => c._id && c._id === data._id);
                if (alreadyExists) return prev;
                return [...prev, { ...data, isPrivateReply: true }];
            });
        });

        return () => {
            SOCKET_CLIENT.off("connect", joinRooms);
            SOCKET_CLIENT.off(SOCKET_EVENTS.NEW_COMMENT);
            SOCKET_CLIENT.off(SOCKET_EVENTS.PRIVATE_REPLY);
        };
    }, [streamId]);

    const handleSend = () => {
        if (!inputMessage.trim()) return;

        if (replyTo) {
            // Private reply — only the target user will see this via socket
            const privateMsg = {
                senderID: userID,
                targetUserID: replyTo.sender?._id || replyTo.senderID,
                streamID: streamId,
                message: inputMessage.trim(),
                replyToCommentId: replyTo._id,
            };
            SOCKET_CLIENT.emit(SOCKET_EVENTS.STREAMER_REPLY, privateMsg);
        } else {
            // Public comment — visible to everyone in the stream
            const msg = {
                senderID: userID,
                streamID: streamId,
                message: inputMessage.trim(),
            };
            SOCKET_CLIENT.emit(SOCKET_EVENTS.COMMENTED, msg);
        }

        setInputMessage("");
        setReplyTo(null);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSend();
        }
    };
    if (loading) {
        return (
            <div className="p-10 max-w-4xl mx-auto">
                <Placeholder.Paragraph rows={10} active />
            </div>
        );
    }

    return (
        <div className={`staff-comments-page flex flex-col ${isModal ? 'h-full bg-white p-4 w-full h-[100dvh]' : 'p-4 md:p-10 max-w-5xl mx-auto min-h-screen'}`}>
            <div className="flex items-center justify-between mb-6 border-b pb-4">
                <div>
                    <h1 className={`${isModal ? 'text-xl' : 'headings-h1'} font-bold text-zinc-950`}>Stream Chat & Comments</h1>
                    <p className="text-zinc-500 font-medium text-sm">{streamMeta?.title || "Live Stream"}</p>
                </div>
                {isModal ? (
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                ) : (
                    <button onClick={() => navigate(-1)} className="btn btn-outline-pri text-sm">
                        Back to Streams
                    </button>
                )}
            </div>

            <div className={`flex-1 bg-white ${isModal ? '' : 'border border-gray-200 rounded-2xl shadow-sm'} overflow-hidden flex flex-col mb-4 min-h-0`}>
                <div className={`flex-1 overflow-y-auto ${isModal ? 'p-2 space-y-4' : 'p-4 md:p-6 space-y-4 max-h-[60vh]'}`}>
                    {comments.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 py-20">
                            <p>No comments yet. Be the first to start the conversation!</p>
                        </div>
                    ) : (
                        comments.map((comment) => (
                            <div key={comment._id} className={`flex flex-col ${comment.sender?._id === userID ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-[80%] rounded-2xl p-3 shadow-sm ${comment.sender?._id === userID
                                    ? 'bg-pink-500 text-white rounded-tr-none'
                                    : 'bg-zinc-100 text-zinc-900 rounded-tl-none'
                                    }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                         <AvatarImage
                                             src={comment.sender?.is_profile_public === false ? null : (comment.comment_sender_image || comment.sender?.profile_image)}
                                             name={comment.comment_sender_name || comment.sender?.name || "User"}
                                             alt="user"
                                             className="w-5 h-5 rounded-full border border-pink-200"
                                             style={{ width: "20px", height: "20px", fontSize: "10px" }}
                                         />
                                        <span className="text-xs font-bold truncate">
                                             {comment.sender?._id === userID
                                                 ? `${comment.comment_sender_name || comment.sender?.name || "User"} (You/Staff)`
                                                 : (comment.sender?.is_profile_public === false
                                                     ? (comment.comment_sender_name || comment.sender?.name || "User").charAt(0).toUpperCase()
                                                     : (comment.comment_sender_name || comment.sender?.name || "User"))}
                                        </span>
                                    </div>

                                    {comment.replyToMessage && (
                                        <div className="bg-black/10 rounded p-2 mb-2 text-[11px] border-l-2 border-pink-300 flex items-start gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3 mt-0.5 shrink-0 opacity-60">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                                            </svg>
                                            <p className="opacity-70 italic truncate">
                                                {comment.replyToMessage}
                                            </p>
                                        </div>
                                    )}

                                    <p className="text-[14px] leading-relaxed break-words">{comment.message}</p>

                                    {comment.isPrivateReply && (
                                        <p className="text-[10px] mt-1 font-bold italic opacity-80 text-pink-700">
                                            Private reply
                                        </p>
                                    )}
                                    <p className={`text-[10px] mt-1 opacity-60 ${comment.sender?._id === userID ? 'text-right' : 'text-left'}`}>
                                        {moment(comment.createdAt).format("hh:mm A")}
                                    </p>
                                </div>
                                {comment.sender?._id !== userID && (
                                    <button
                                        onClick={() => setReplyTo(comment)}
                                        className="text-[11px] text-pink-500 font-bold hover:underline mt-1 ml-2 flex items-center gap-1"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                                        </svg>
                                        Private Reply
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                    <div ref={commentsEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-gray-100 bg-zinc-50 relative">
                    {replyTo && (
                        <div className="absolute top-[-44px] left-0 right-0 bg-pink-50 text-pink-700 text-xs p-2 flex justify-between items-center border-t border-pink-100 px-6">
                            <span className="flex items-center gap-1">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                                </svg>
                                <strong>Private reply</strong>&nbsp;to <strong>{replyTo.sender?.name}</strong>: {replyTo.message.substring(0, 30)}...
                            </span>
                            <button onClick={() => setReplyTo(null)} className="text-pink-800 font-bold ml-4">✕</button>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={replyTo ? `Private reply to ${replyTo.sender?.name}...` : "Write a public comment..."}
                            className="flex-1 bg-white border border-gray-300 rounded-full px-5 py-2.5 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 text-[14px]"
                        />
                        <button
                            onClick={handleSend}
                            disabled={!inputMessage.trim()}
                            className="btn btn-pri rounded-full w-12 h-12 flex items-center justify-center p-0 disabled:opacity-50"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
                <h3 className="text-sm font-bold text-zinc-900 mb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.835a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                    </svg>
                    Staff Interaction Guide
                </h3>
                <ul className="text-xs text-zinc-600 space-y-1 ml-6 list-disc">
                    <li>Click <strong>"Private Reply"</strong> on any comment to send a message <strong>only that user can see</strong>.</li>
                    <li>Messages sent without a reply target are <strong>public comments</strong> visible to all viewers.</li>
                    <li>Always maintain a professional tone while interacting with the audience.</li>
                </ul>
            </div>
        </div>
    );
}
